import React, { useState, useEffect } from 'react';
import { Sociedad, SociedadErrors, UpdateSociedadRequest } from '../types';
import { sociedadService } from '../services/sociedadService';
import { apiUtils } from '../config/api';
import { sociedadStorage } from '../stores/sociedadStorage';
import { accionFormativaStorage } from '../stores/accionFormativaStorage';
import { cursoStorage } from '../stores/cursoStorage';
import Modal from '../components/Modal';
import SnackBar from '../components/SnackBar';
import FormHeader from '../components/FormHeader';
import './sociedad-form.css';

interface SociedadFormProps {
  onSubmit: (data: Sociedad, tipoFormacion: 'accionFormativa' | 'curso') => void;
  initialData?: Partial<Sociedad>;
  onError?: (error: string) => void;
  onNavigateToStart?: () => void;
}

/**
 * @fileoverview Componente de formulario para recopilar datos del solicitante
 * @description Permite al usuario ingresar información personal y de contacto
 */

/**
 * Props del componente SociedadForm
 * @interface SociedadFormProps
 */
/**
 * Componente de formulario para datos del solicitante
 * @param props - Propiedades del componente
 * @returns JSX.Element
 */
const SociedadForm: React.FC<SociedadFormProps> = ({ onSubmit, initialData = {}, onError, onNavigateToStart }) => {
  const [formData, setFormData] = useState<Sociedad>({
    cif: initialData.cif || '',
    razonSocial: initialData.razonSocial || '',
    direccion: initialData.direccion || '',
    localidad: initialData.localidad || '',
    provincia: initialData.provincia || '',
    codigoPostal: initialData.codigoPostal || '',
    personaContacto: initialData.personaContacto || '',
    telefono: initialData.telefono || '',
    email: initialData.email || '',
    fileResolucion: initialData.fileResolucion || null,
    fileTextoSeguros: initialData.fileTextoSeguros || null,
    condicionesIdAsociacion: initialData.condicionesIdAsociacion || '',
    fileResumen: initialData.fileResumen || '',
  });

  // Función para asegurar que los datos del formulario siempre tengan valores válidos
  const ensureFormDataIntegrity = (data: Sociedad): Sociedad => ({
    cif: data.cif || '',
    razonSocial: data.razonSocial || '',
    direccion: data.direccion || '',
    localidad: data.localidad || '',
    provincia: data.provincia || '',
    codigoPostal: data.codigoPostal || '',
    personaContacto: data.personaContacto || '',
    telefono: data.telefono || '',
    email: data.email || '',
    fileResolucion: data.fileResolucion || null,
    fileTextoSeguros: data.fileTextoSeguros || null,
    condicionesIdAsociacion: data.condicionesIdAsociacion || '',
    fileResumen: data.fileResumen || null,
  });

  const [errors, setErrors] = useState<SociedadErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [existingSociedad, setExistingSociedad] = useState<Sociedad | null>(null);
  const [sociedadId, setSociedadId] = useState<number | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'warning'>('success');
  const [errorMessage, setErrorMessage] = useState('');

  // Estados para archivos
  const [fileResolucion, setFileResolucion] = useState<File | null>(null);
  const [fileTextoSeguros, setFileTextoSeguros] = useState<File | null>(null);

  // Estado para tipo de formación
  const [tipoFormacion, setTipoFormacion] = useState<'accionFormativa' | 'curso'>('accionFormativa');
  // Estado para deshabilitar la selección del tipo de formación
  const [isTipoFormacionDisabled, setIsTipoFormacionDisabled] = useState(false);

  /**
   * Carga los datos guardados del store al montar el componente
   */
  useEffect(() => {
    const storedSociedad = sociedadStorage.getSociedad();
    // Solo cargar del store si no hay initialData o si initialData está vacío
    // initialData tiene prioridad sobre el store
    if (storedSociedad && (!initialData || Object.keys(initialData).length === 0)) {
      const safeData = ensureFormDataIntegrity(storedSociedad);
      setFormData(safeData);

      // Si hay ID de sociedad, establecerlo
      if (storedSociedad.idSociedad) {
        setSociedadId(storedSociedad.idSociedad);
        setIsUpdateMode(true);
      }
    }

    // Verificar si ya existe un ID de acción formativa o curso guardado
    const storedAccionFormativaId = accionFormativaStorage.getAccionFormativaId();
    const storedCursoId = cursoStorage.getCursoId();

    // Verificar si hay un tipo de formación guardado
    const storedTipoFormacion = sociedadStorage.getTipoFormacion();

    // Si existe un ID de acción formativa o curso, deshabilitar la selección
    if (storedAccionFormativaId || storedCursoId) {
      setIsTipoFormacionDisabled(true);

      // Establecer el tipo de formación según el ID encontrado
      if (storedAccionFormativaId) {
        setTipoFormacion('accionFormativa');
        sociedadStorage.saveTipoFormacion('accionFormativa');
      } else if (storedCursoId) {
        setTipoFormacion('curso');
        sociedadStorage.saveTipoFormacion('curso');
      }
    } else if (storedTipoFormacion) {
      // Si no hay IDs pero hay un tipo de formación guardado, también deshabilitar
      // Esto significa que el usuario ya navegó y seleccionó un tipo, pero aún no se ha creado
      setIsTipoFormacionDisabled(true);
      setTipoFormacion(storedTipoFormacion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar, initialData se maneja en el estado inicial

  /**
   * Maneja cambios en los inputs y actualiza el estado del formulario
   * @param e - Evento de cambio del input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value
    };

    setFormData(updatedData);

    // Guardar en LocalStorage cuando cambian los datos
    sociedadStorage.saveSociedad(updatedData);

    // Clear error when user starts typing
    if (errors[name as keyof SociedadErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Maneja el blur del campo CIF para verificar si existe y autocompletar
   * @param e - Evento de blur del input
   */
  const handleCifBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const cif = e.target.value.trim().toUpperCase();

    // Solo verificar si el CIF tiene un formato válido y no estamos en modo actualización
    if (cif && !isUpdateMode && cif.length >= 9) {
      try {
        const response = await sociedadService.obtenerSociedad(cif);

        if (response.success && response.data) {
          const sociedadExistente = response.data.sociedad;

          // Autocompletar campos del formulario
          const updatedData = {
            ...formData,
            razonSocial: sociedadExistente.razonSocial || formData.razonSocial,
            direccion: sociedadExistente.direccion || formData.direccion,
            localidad: sociedadExistente.localidad || formData.localidad,
            provincia: sociedadExistente.provincia || formData.provincia,
            codigoPostal: sociedadExistente.codigoPostal || formData.codigoPostal,
            personaContacto: sociedadExistente.personaContacto || formData.personaContacto,
            telefono: sociedadExistente.telefono || formData.telefono,
            email: sociedadExistente.email || formData.email,
            condicionesIdAsociacion: sociedadExistente.condicionesIdAsociacion || formData.condicionesIdAsociacion,
            fileResolucion: sociedadExistente.fileResolucion || formData.fileResolucion,
            fileTextoSeguros: sociedadExistente.fileTextoSeguros || formData.fileTextoSeguros,
            cif: cif
          };

          setFormData(updatedData);

          // Si existe ID de sociedad, establecerlo
          if (sociedadExistente.idSociedad) {
            setSociedadId(sociedadExistente.idSociedad);
            const sociedadToSave = {
              ...updatedData,
              idSociedad: sociedadExistente.idSociedad
            };
            sociedadStorage.saveSociedad(sociedadToSave);
          } else {
            // Guardar datos autocompletados aunque no haya ID
            sociedadStorage.saveSociedad(updatedData);
          }
        }
      } catch (error) {
        // Silently fail - no mostrar error si no se encuentra la sociedad
        // Es normal que no exista si es una nueva sociedad
      }
    }
  };

  /**
   * Maneja la selección de archivos
   * @param e - Evento de cambio del input de archivo
   * @param fileType - Tipo de archivo ('resolucion' o 'textoSeguros')
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'resolucion' | 'textoSeguros') => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === 'resolucion') {
        setFileResolucion(file);
        const updatedData = {
          ...formData,
          fileResolucion: file.name
        };
        setFormData(updatedData);
        sociedadStorage.saveSociedad(updatedData);
      } else {
        setFileTextoSeguros(file);
        const updatedData = {
          ...formData,
          fileTextoSeguros: file.name
        };
        setFormData(updatedData);
        sociedadStorage.saveSociedad(updatedData);
      }

      // Clear error when user selects a file
      const errorKey = fileType === 'resolucion' ? 'fileResolucion' : 'fileTextoSeguros';
      if (errors[errorKey as keyof SociedadErrors]) {
        setErrors(prev => ({
          ...prev,
          [errorKey]: undefined
        }));
      }
    }
  };

  /**
   * Maneja cambios en el select de asociación
   * @param e - Evento de cambio del select
   */
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const updatedData = {
      ...formData,
      [name]: value
    };

    setFormData(updatedData);

    // Guardar en LocalStorage cuando cambian los datos
    sociedadStorage.saveSociedad(updatedData);

    // Clear error when user makes a selection
    if (errors[name as keyof SociedadErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Maneja el cambio del tipo de formación
   * @param e - Evento de cambio del radio button
   */
  const handleTipoFormacionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoTipo = e.target.value as 'accionFormativa' | 'curso';

    setTipoFormacion(nuevoTipo);

    // Guardar el tipo de formación en localStorage
    sociedadStorage.saveTipoFormacion(nuevoTipo);

    // Guardar también los datos de la sociedad
    const updatedData = {
      ...formData
    };
    sociedadStorage.saveSociedad(updatedData);
  };

  /**
   * Valida los datos del formulario y retorna errores de validación
   * @returns boolean - true si la validación es exitosa
   */
  const validateForm = (): boolean => {
    const newErrors: SociedadErrors = {};

    // En modo actualización, solo validar campos modificables
    if (isUpdateMode) {
      // Validación de persona de contacto
      if (!formData.personaContacto || !formData.personaContacto.trim()) {
        newErrors.personaContacto = 'La persona de contacto es obligatoria';
      } else if (formData.personaContacto.trim().length < 2) {
        newErrors.personaContacto = 'La persona de contacto debe tener al menos 2 caracteres';
      }

      // Validación de teléfono
      if (!formData.telefono || !formData.telefono.trim()) {
        newErrors.telefono = 'El teléfono es obligatorio';
      } else if (!/^[+]?[\d\s-()]{9,}$/.test(formData.telefono.trim())) {
        newErrors.telefono = 'El formato del teléfono no es válido';
      }

      // Validación de email
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'El email es obligatorio';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'El formato del email no es válido';
      }

      // Validación de dirección
      if (!formData.direccion || !formData.direccion.trim()) {
        newErrors.direccion = 'La dirección es obligatoria';
      } else if (formData.direccion.trim().length < 5) {
        newErrors.direccion = 'La dirección debe tener al menos 5 caracteres';
      }

      // Validación de localidad
      if (!formData.localidad || !formData.localidad.trim()) {
        newErrors.localidad = 'La localidad es obligatoria';
      } else if (formData.localidad.trim().length < 2) {
        newErrors.localidad = 'La localidad debe tener al menos 2 caracteres';
      }

      // Validación de provincia
      if (!formData.provincia || !formData.provincia.trim()) {
        newErrors.provincia = 'La provincia es obligatoria';
      } else if (formData.provincia.trim().length < 2) {
        newErrors.provincia = 'La provincia debe tener al menos 2 caracteres';
      }

      // Validación de código postal
      if (!formData.codigoPostal || !formData.codigoPostal.trim()) {
        newErrors.codigoPostal = 'El código postal es obligatorio';
      } else if (!/^\d{5}$/.test(formData.codigoPostal.trim())) {
        newErrors.codigoPostal = 'El código postal debe tener 5 dígitos';
      }

      // Validación opcional de archivos en modo actualización
      // Solo validar si se seleccionan nuevos archivos
      if (fileResolucion && !fileResolucion.name) {
        newErrors.fileResolucion = 'El archivo de resolución no es válido';
      }
      if (fileTextoSeguros && !fileTextoSeguros.name) {
        newErrors.fileTextoSeguros = 'El archivo de texto de seguros no es válido';
      }
    } else {
      // Validación completa para creación
      // Validación de razón social
      if (!formData.razonSocial.trim()) {
        newErrors.razonSocial = 'La razón social es obligatoria';
      } else if (formData.razonSocial.trim().length < 2) {
        newErrors.razonSocial = 'La razón social debe tener al menos 2 caracteres';
      }

      // Validación de CIF/NIF
      if (!formData.cif.trim()) {
        newErrors.cif = 'El CIF/NIF es obligatorio';
      } else {
        const cifNif = formData.cif.trim();
        const CIF_REGEX = /^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/i;
        const NIF_REGEX = /^\d{8}[A-Z]$/i;

        if (!CIF_REGEX.test(cifNif) && !NIF_REGEX.test(cifNif)) {
          newErrors.cif = 'El formato del CIF/NIF no es válido';
        }
      }

      // Validación de dirección
      if (!formData.direccion.trim()) {
        newErrors.direccion = 'La dirección es obligatoria';
      } else if (formData.direccion.trim().length < 5) {
        newErrors.direccion = 'La dirección debe tener al menos 5 caracteres';
      }

      // Validación de localidad
      if (!formData.localidad.trim()) {
        newErrors.localidad = 'La localidad es obligatoria';
      } else if (formData.localidad.trim().length < 2) {
        newErrors.localidad = 'La localidad debe tener al menos 2 caracteres';
      }

      // Validación de provincia
      if (!formData.provincia.trim()) {
        newErrors.provincia = 'La provincia es obligatoria';
      } else if (formData.provincia.trim().length < 2) {
        newErrors.provincia = 'La provincia debe tener al menos 2 caracteres';
      }

      // Validación de código postal
      if (!formData.codigoPostal.trim()) {
        newErrors.codigoPostal = 'El código postal es obligatorio';
      } else if (!/^\d{5}$/.test(formData.codigoPostal.trim())) {
        newErrors.codigoPostal = 'El código postal debe tener 5 dígitos';
      }

      // Validación de persona de contacto
      if (!formData.personaContacto || !formData.personaContacto.trim()) {
        newErrors.personaContacto = 'La persona de contacto es obligatoria';
      } else if (formData.personaContacto.trim().length < 2) {
        newErrors.personaContacto = 'La persona de contacto debe tener al menos 2 caracteres';
      }

      // Validación de teléfono
      if (!formData.telefono || !formData.telefono.trim()) {
        newErrors.telefono = 'El teléfono es obligatorio';
      } else if (!/^[+]?[\d\s-()]{9,}$/.test(formData.telefono.trim())) {
        newErrors.telefono = 'El formato del teléfono no es válido';
      }

      // Validación de email
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'El email es obligatorio';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = 'El formato del email no es válido';
      }

      // fileResolucion es opcional, no se valida
      // fileTextoSeguros es opcional, no se valida
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Convierte los datos del formulario al formato de la API para creación
   * @param data - Datos del formulario
   * @returns Datos en formato de API para creación
   */
  const convertToCreateApiFormat = (data: Sociedad) => ({
    razonSocial: data.razonSocial,
    cif: data.cif.toUpperCase(),
    direccion: data.direccion,
    localidad: data.localidad,
    provincia: data.provincia,
    codigoPostal: data.codigoPostal,
    personaContacto: data.personaContacto,
    telefono: data.telefono,
    email: data.email,
    fileResolucion: fileResolucion || null, // Opcional: solo incluir si hay archivo
    fileTextoSeguros: fileTextoSeguros || null, // Opcional: solo incluir si hay archivo
    condicionesIdAsociacion: data.condicionesIdAsociacion
  });

  /**
   * Convierte los datos del formulario al formato de la API para actualización
   * @param data - Datos del formulario
   * @returns Datos en formato de API para actualización
   */
  const convertToUpdateApiFormat = (data: Sociedad) => {
    // Asegurar que condicionesIdAsociacion se envíe correctamente (incluso si es cadena vacía)
    const condicionesIdAsociacion = data.condicionesIdAsociacion !== undefined && data.condicionesIdAsociacion !== null
      ? data.condicionesIdAsociacion
      : '';

    const apiData = {
      personaContacto: data.personaContacto || '',
      telefono: data.telefono || '',
      email: data.email || '',
      condicionesIdAsociacion: condicionesIdAsociacion, // Enviar el valor tal cual está en formData
      razonSocial: data.razonSocial || '', // Requerido para crear carpeta de archivos
      cif: data.cif.toUpperCase() || '', // Requerido para crear carpeta de archivos
      direccion: data.direccion || '',
      localidad: data.localidad || '',
      provincia: data.provincia || '',
      codigoPostal: data.codigoPostal || '',
      fileResolucion: fileResolucion || null, // Solo incluir si hay archivo nuevo
      fileTextoSeguros: fileTextoSeguros || null // Solo incluir si hay archivo nuevo
    };

    return apiData;
  };

  /**
   * Convierte los datos de la API al formato del formulario
   * @param data - Datos de la API (en camelCase)
   * @returns Datos en formato de formulario
   */
  const convertFromApiFormat = (data: any): Sociedad => ({
    cif: data.cif.toUpperCase() || '',
    razonSocial: data.razonSocial || '',
    direccion: data.direccion || '',
    localidad: data.localidad || '',
    provincia: data.provincia || '',
    codigoPostal: data.codigoPostal || '',
    personaContacto: data.personaContacto || '',
    telefono: data.telefono || '',
    email: data.email || '',
    fileResolucion: data.fileResolucion || null,
    fileTextoSeguros: data.fileTextoSeguros || null,
    condicionesIdAsociacion: data.condicionesIdAsociacion || 'CECAP-CYL',
    fileResumen: data.fileResumen || null,
  });

  /**
   * Maneja la creación de una nueva sociedad
   */
  const handleCreateSociedad = async () => {
    try {
      setIsSubmitting(true);
      const apiData = convertToCreateApiFormat(formData);
      const response = await sociedadService.crearSociedad(apiData);

      if (response.success) {
        // Sociedad creada exitosamente - mostrar SnackBar
        setSuccessMessage(response.message || 'Sociedad creada exitosamente');
        setSnackbarType('success');
        setShowSuccessSnackbar(true);

        // Guardar sociedad en LocalStorage
        // Asegurar que los nombres de archivos se guarden correctamente
        const sociedadToSave = {
          ...formData,
          idSociedad: response.data?.idSociedad,
          // Guardar nombres de archivos desde los objetos File
          fileResolucion: fileResolucion?.name || formData.fileResolucion || null,
          fileTextoSeguros: fileTextoSeguros?.name || formData.fileTextoSeguros || null
        };
        sociedadStorage.saveSociedad(sociedadToSave);

        // Guardar el tipo de formación en localStorage
        sociedadStorage.saveTipoFormacion(tipoFormacion);

        // Continuar navegación después de mostrar el mensaje
        setTimeout(() => {
          onSubmit(sociedadToSave, tipoFormacion);
        }, 2000);
      } else {
        // Sociedad ya existe - mostrar Modal con datos existentes
        setErrorMessage(response.message || 'Ya existe una sociedad con este CIF');
        const existingData = convertFromApiFormat(response.sociedadExistente || response);
        const safeData = ensureFormDataIntegrity(existingData);
        setExistingSociedad(safeData);
        setFormData(safeData);

        // Extraer ID de la sociedad existente si está disponible
        if (response.sociedadExistente?.idSociedad) {
          setSociedadId(response.sociedadExistente.idSociedad);

          // Guardar sociedad existente en LocalStorage
          const existingSociedad = {
            ...safeData,
            idSociedad: response.sociedadExistente.idSociedad
          };
          sociedadStorage.saveSociedad(existingSociedad);
        }

        setIsUpdateMode(true);
        setShowExistingModal(true);
      }
    } catch (error) {
      if (onError) {
        onError(apiUtils.handleError(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Maneja la actualización de una sociedad existente
   */
  const handleUpdateSociedad = async () => {
    try {
      setIsSubmitting(true);

      const apiData = convertToUpdateApiFormat(formData);

      if (!sociedadId) {
        throw new Error('ID de sociedad no disponible para actualización');
      }

      const response = await sociedadService.actualizarSociedad(sociedadId, apiData);

      if (response.success) {
        // Sociedad actualizada exitosamente - mostrar SnackBar
        setSuccessMessage(response.message || 'Sociedad actualizada exitosamente');
        setSnackbarType('warning');
        setShowSuccessSnackbar(true);

        // Guardar sociedad actualizada en LocalStorage
        // Asegurar que los nombres de archivos se preserven (nuevo archivo o el existente)
        const updatedSociedad = {
          ...formData,
          idSociedad: sociedadId,
          // Preservar nombres de archivos: si hay nuevo archivo, usar su nombre; si no, mantener el existente
          fileResolucion: fileResolucion?.name || formData.fileResolucion || null,
          fileTextoSeguros: fileTextoSeguros?.name || formData.fileTextoSeguros || null
        };
        sociedadStorage.saveSociedad(updatedSociedad);

        // Guardar el tipo de formación en localStorage
        sociedadStorage.saveTipoFormacion(tipoFormacion);

        // Continuar navegación después de mostrar el mensaje
        setTimeout(() => {
          onSubmit(updatedSociedad, tipoFormacion);
        }, 2000);
      } else {
        if (onError) {
          onError(response.message || 'Error al actualizar la sociedad');
        }
      }
    } catch (error) {
      if (onError) {
        onError(apiUtils.handleError(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Maneja el envío del formulario
   * @param e - Evento de envío del formulario
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (isUpdateMode) {
        handleUpdateSociedad();
      } else {
        handleCreateSociedad();
      }
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <FormHeader onLogoClick={onNavigateToStart} />
        <h2 className="form-subtitle">DATOS DEL SOLICITANTE</h2>

        {isUpdateMode && (
          <div className="update-mode-notice">
            <p>
              <strong>Modo de actualización</strong>
            </p>
            <p>
              Puede modificar los campos de contacto (Persona de contacto, Teléfono y Email), la dirección, localidad, provincia, código postal, la Asociación, el tipo de formación y los archivos adjuntos.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cif">
                CIF / NIF <span className="required">*</span>
              </label>
              <input
                type="text"
                id="cif"
                name="cif"
                value={formData.cif}
                onChange={handleInputChange}
                onBlur={handleCifBlur}
                className={errors.cif ? 'error' : ''}
                required={!isUpdateMode}
                disabled={isUpdateMode}
                aria-describedby={errors.cif ? 'cif-error' : undefined}
              />
              {errors.cif && <span id="cif-error" className="error-message">{errors.cif}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="razonSocial">
              Razón social <span className="required">*</span>
            </label>
            <input
              type="text"
              id="razonSocial"
              name="razonSocial"
              value={formData.razonSocial}
              onChange={handleInputChange}
              className={errors.razonSocial ? 'error' : ''}
              required={!isUpdateMode}
              disabled={isUpdateMode}
              aria-describedby={errors.razonSocial ? 'razonSocial-error' : undefined}
            />
            {errors.razonSocial && <span id="razonSocial-error" className="error-message">{errors.razonSocial}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="direccion">
              Dirección <span className="required">*</span>
            </label>
            <input
              type="text"
              id="direccion"
              name="direccion"
              value={formData.direccion}
              onChange={handleInputChange}
              className={errors.direccion ? 'error' : ''}
              required
              aria-describedby={errors.direccion ? 'direccion-error' : undefined}
            />
            {errors.direccion && <span id="direccion-error" className="error-message">{errors.direccion}</span>}
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="localidad">
                Localidad <span className="required">*</span>
              </label>
              <input
                type="text"
                id="localidad"
                name="localidad"
                value={formData.localidad}
                onChange={handleInputChange}
                className={errors.localidad ? 'error' : ''}
                required
                aria-describedby={errors.localidad ? 'localidad-error' : undefined}
              />
              {errors.localidad && <span id="localidad-error" className="error-message">{errors.localidad}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="provincia">
                Provincia <span className="required">*</span>
              </label>
              <input
                type="text"
                id="provincia"
                name="provincia"
                value={formData.provincia}
                onChange={handleInputChange}
                className={errors.provincia ? 'error' : ''}
                required
                aria-describedby={errors.provincia ? 'provincia-error' : undefined}
              />
              {errors.provincia && <span id="provincia-error" className="error-message">{errors.provincia}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="codigoPostal">
                Código postal <span className="required">*</span>
              </label>
              <input
                type="text"
                id="codigoPostal"
                name="codigoPostal"
                value={formData.codigoPostal}
                onChange={handleInputChange}
                className={errors.codigoPostal ? 'error' : ''}
                required
                maxLength={5}
                aria-describedby={errors.codigoPostal ? 'codigoPostal-error' : undefined}
              />
              {errors.codigoPostal && <span id="codigoPostal-error" className="error-message">{errors.codigoPostal}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="personaContacto">
                Persona de contacto <span className="required">*</span>
              </label>
              <input
                type="text"
                id="personaContacto"
                name="personaContacto"
                value={formData.personaContacto}
                onChange={handleInputChange}
                className={errors.personaContacto ? 'error' : ''}
                required
                aria-describedby={errors.personaContacto ? 'personaContacto-error' : undefined}
              />
              {errors.personaContacto && <span id="personaContacto-error" className="error-message">{errors.personaContacto}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="telefono">
                Teléfono <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleInputChange}
                className={errors.telefono ? 'error' : ''}
                required
                aria-describedby={errors.telefono ? 'telefono-error' : undefined}
              />
              {errors.telefono && <span id="telefono-error" className="error-message">{errors.telefono}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                required
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && <span id="email-error" className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="asociacion">
              Asociación <span className="required">*</span>
            </label>
            <select
              id="condicionesIdAsociacion"
              name="condicionesIdAsociacion"
              value={formData.condicionesIdAsociacion}
              onChange={handleSelectChange}
              required
            >
              <option value=""></option>
              <option value="SIN-CECAP">SIN ASOCIACION</option>
              <option value="CECAP-CYL">CECAP CASTILLA Y LEON</option>
              <option value="CECAP-AND">CECAP ANDALUCIA</option>
              <option value="CECAP-VAL">CECAP COMUNIDAD VALENCIANA</option>
              <option value="CECAP-EXT">CECAP EXTREMADURA</option>
              <option value="AEGEF">ASOCIACION ESTATAL DE GRANDES EMPRESAS DE FORMACION</option>
              <option value="FMASTER">FORMASTER</option>
            </select>
          </div>

          {/* Selección del tipo de formación */}
          <div className="form-group">
            <label>Tipo de formación <span className="required">*</span></label>
            {isTipoFormacionDisabled && (
              <div className="update-mode-notice" style={{ fontSize: '14px' }}>
                Ya se ha seleccionado un tipo de formación para esta solicitud. Pulse <strong style={{ fontSize: '14px' }}>cancelar</strong> para una nueva solicitud.
              </div>
            )}
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="accionFormativa"
                  name="tipoFormacion"
                  value="accionFormativa"
                  checked={tipoFormacion === 'accionFormativa'}
                  onChange={handleTipoFormacionChange}
                  disabled={isTipoFormacionDisabled}
                />
                <label htmlFor="accionFormativa" style={isTipoFormacionDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                  Acción Formativa
                </label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="curso"
                  name="tipoFormacion"
                  value="curso"
                  checked={tipoFormacion === 'curso'}
                  onChange={handleTipoFormacionChange}
                  disabled={isTipoFormacionDisabled}
                />
                <label htmlFor="curso" style={isTipoFormacionDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                  Curso
                </label>
              </div>
            </div>
          </div>

          {/* Archivos de resolución y texto de seguros */}
          <div className="form-group">
            <label htmlFor="fileResolucion">
              PDF/Excel para cursos de la acción formativa <span style={{ fontSize: '0.9em', color: '#666' }}>(opcional)</span>
            </label>
            <input
              type="file"
              id="fileResolucion"
              name="fileResolucion"
              onChange={(e) => handleFileChange(e, 'resolucion')}
              className={errors.fileResolucion ? 'error' : ''}
              accept=".pdf,.xls,.xlsx"
              aria-describedby={errors.fileResolucion ? 'fileResolucion-error' : undefined}
            />
            {errors.fileResolucion && <span id="fileResolucion-error" className="error-message">{errors.fileResolucion}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="fileTextoSeguros">
              PDF en el que se especifique los seguros necesarios <span style={{ fontSize: '0.9em', color: '#666' }}>(opcional)</span>
            </label>
            <input
              type="file"
              id="fileTextoSeguros"
              name="fileTextoSeguros"
              onChange={(e) => handleFileChange(e, 'textoSeguros')}
              accept=".pdf"
            />
            {errors.fileTextoSeguros && <span id="fileTextoSeguros-error" className="error-message">{errors.fileTextoSeguros}</span>}
          </div>


          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onNavigateToStart}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary-form-sociedad"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Procesando...'
                : isUpdateMode
                  ? 'Actualizar y Continuar'
                  : 'Continuar'
              }
            </button>
          </div>
        </form>

        {/* Modal para sociedad existente */}
        <Modal
          isOpen={showExistingModal}
          onClose={() => setShowExistingModal(false)}
          title="Solicitante ya registrado"
        >
          <div style={{ color: '#856404', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 16px 0' }}>
              <strong>⚠️ {errorMessage}</strong>
            </p>
            <div style={{
              padding: '12px',
              backgroundColor: '#fff3cd',
              borderRadius: '4px',
              border: '1px solid #ffeaa7',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '4px 0' }}><strong>CIF:</strong> {existingSociedad?.cif}</p>
              <p style={{ margin: '4px 0' }}><strong>Razón Social:</strong> {existingSociedad?.razonSocial}</p>
              <p style={{ margin: '4px 0' }}><strong>Email:</strong> {existingSociedad?.email}</p>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
              Los datos han sido cargados en el formulario. Puede actualizarlos si es necesario y continuar.
            </p>
            <div style={{ textAlign: 'right' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowExistingModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>

        {/* SnackBar para éxito */}
        <SnackBar
          isOpen={showSuccessSnackbar}
          onClose={() => setShowSuccessSnackbar(false)}
          message={successMessage}
          type={snackbarType}
          duration={4000}
          position="bottom-center"
        />
      </div>
    </div>
  );
};

export default SociedadForm;
